import {useEffect, useState, type FC} from "react"
import {Stack, useTheme} from "@mui/material"
import {AccountPanel, Button, SupportModal} from "../../components"
import NavigationTopBar from "../../components/organisms/NavigationTopBar/NavigationTopBar"
import {Outlet, useLocation, useNavigate} from "react-router-dom"
import Overview from "../Overview/Overview"
import Discover from "../Discover/Discover"
import useHomepageTabStore from "../../stores/homepageTabStore"
import ExpertBio from "../../components/organisms/ExpertBio/ExpertBio"
import GuidedTour from "../../components/organisms/GuidedTour/GuidedTour"
import {useConfigProvider} from "../../config"
import {decodeToken} from "react-jwt"
import {Token} from "../../types"
import {useLocalStorage, useSessionStorage} from "usehooks-ts"
import {StorageData} from "../../hooks/useStorage"
import {useAppContext} from "../../context/AppContext"
import {Trans, useTranslation} from "react-i18next"
import useExpertBioStore from "../../stores/expertBioStore"
import {expertListStore} from "../../stores/expertListStore"
import NewFeatureRide from "../../components/organisms/NewFeatureRide/NewFeatureRide"

const Home: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const {t} = useTranslation()
  const {token} = useConfigProvider()
  const {useGuidedTourStore} = useAppContext()
  const run = useGuidedTourStore((state) => state.run)
  const setRun = useGuidedTourStore((state) => state.setRun)
  const [runNewFeatureLanguage, setRunNewFeatureLanguage] = useState(false)
  const completedTour = useGuidedTourStore((state) => state.completedTour)
  const setCompletedTour = useGuidedTourStore((state) => state.setCompletedTour)
  const [targetsExists, setTargetsExist] = useState(false)
  const {currentTab, setCurrentTab, previousTab, setPreviousTab} =
    useHomepageTabStore()
  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)
  const theme = useTheme()
  const currentPath = location.pathname
  const decoded = decodeToken<Token>(token)
  const [completedContactForm, setCompletedContactForm] = useSessionStorage<
    boolean | undefined
  >(StorageData.completedContactForm, undefined)
  const [completedPreferencesForm, setCompletedPreferencesForm] =
    useSessionStorage<boolean | undefined>(
      StorageData.completedPreferences,
      undefined,
    )
  const [skippedPreferencesForm] = useSessionStorage<boolean | undefined>(
    StorageData.skippedPreferences,
    undefined,
  )
  const [completedNewFeature, setCompletedNewFeature] = useLocalStorage<
    boolean | undefined
  >(StorageData.completedNewFeature, undefined)
  const {setShowFindExpert, setDisplayExpert} = useExpertBioStore()
  const {clearSearchTerm, clearRegion} = expertListStore()

  useEffect(() => {
    const localStoragecompletedTour = localStorage.getItem(
      StorageData.completedTour,
    )

    const isCompleted =
      localStoragecompletedTour !== null &&
      localStoragecompletedTour !== "undefined"

    setCompletedTour(isCompleted)
  }, [])

  useEffect(() => {
    if (!!decoded?.completedContactForm) {
      setCompletedContactForm(true)
    }
    if (!!decoded?.completedPreferences) {
      setCompletedPreferencesForm(true)
    }
  }, [decoded])

  const handleTabChange = (newTab: string) => {
    setCurrentTab(newTab)
    setPreviousTab("")
    navigate("/")
  }

  const handleCurrentTab = () => {
    if (previousTab !== "" && currentTab !== previousTab) {
      setCurrentTab(previousTab)
      setPreviousTab("")
    }
  }

  useEffect(() => {
    handleCurrentTab()
  }, [])
  const [actionFromNavBar, setActionFromNavBar] = useState(false)

  useEffect(() => {
    const handleNavButtonClickedEvent = (event: any) => {
      if (event instanceof CustomEvent) {
        setShowFindExpert(false)
        setDisplayExpert(false)
        clearRegion()
        clearSearchTerm()
        setActionFromNavBar(true)
        setCurrentTab("")
      }
    }
    window.addEventListener(
      "mycr-navigation:navButtonClicked",
      handleNavButtonClickedEvent,
    )
    return () => {
      window.removeEventListener(
        "mycr-navigation:navButtonClicked",
        handleNavButtonClickedEvent,
      )
    }
  }, [])

  useEffect(() => {
    if (actionFromNavBar) {
      setActionFromNavBar(false)
      setCurrentTab("Overview")
    }
  }, [actionFromNavBar])

  useEffect(() => {
    const checkTargets = () => {
      const welcome = document.querySelector("#user-welcome")
      const logo = document.querySelector("#my-deliverables")
      if (welcome && logo && !completedTour) {
        setTargetsExist(true)
        setRun(true)
      }
    }

    checkTargets()
    const interval = setInterval(checkTargets, 1000)
    return () => clearInterval(interval)
  }, [completedTour])

  useEffect(() => {
    const welcome = document.querySelector("#user-welcome")
    const logo = document.querySelector("#my-deliverables")

    if (welcome && logo && completedTour && !completedNewFeature) {
      setTargetsExist(true)
      setRunNewFeatureLanguage(true)
    }
  }, [completedTour])

  return (
    <Stack
      component="main"
      direction="row"
      height="100%"
      width="100vw"
      maxWidth="100%"
    >
      {targetsExists &&
        completedContactForm &&
        (completedPreferencesForm || skippedPreferencesForm) &&
        !completedTour && (
          <GuidedTour
            continuous
            run={run}
            setRun={setRun}
            steps={[
              {
                title: t("Profile"),
                target: "#user-welcome",
                content: (
                  <div>
                    <Trans
                      i18nKey="guidedTourWelcome"
                      components={{
                        SupportLink: (
                          <Button
                            sx={{
                              display: "inline",
                              backgroundColor: "white",
                              textDecoration: "underline",
                              margin: "0",
                              padding: "0",
                              color: "#226ca6",
                              "&:hover": {
                                backgroundColor: "white",
                                textDecoration: "underline",
                                color: "#226ca6",
                              },
                            }}
                            onClick={() => {
                              localStorage.setItem(
                                StorageData.completedTour,
                                "true",
                              )
                              setRun(false)
                              setCompletedTour(true)
                              setShowSupportModal(true)
                            }}
                          >
                            contact us.
                          </Button>
                        ),
                      }}
                    />
                  </div>
                ),
                placement: "bottom-end",
                spotlightPadding: 0,
                disableBeacon: true,
              },
              {
                title: t("Account team"),
                target: "#account-team",
                content: t(
                  "You can find and contact your Control Risks account team here.",
                ),
                placement: "left",
                spotlightPadding: 0,
                disableBeacon: true,
              },
              {
                title: t("My deliverables"),
                target: "#my-deliverables",
                content: t(
                  "Your most recent 5 deliverables. Clicking on the project name takes you to the project folder, clicking on the deliverable name opens the file.",
                ),
                placement: "bottom-start",
                spotlightPadding: 0,
                disableBeacon: true,
              },
              {
                title: t("Contact an expert"),
                target: "#ask-an-expert",
                content: t(
                  "If you have any questions you can contact your expert here.",
                ),
                placement: "right",
                spotlightPadding: 0,
                disableBeacon: true,
              },
              {
                title: t("Projects"),
                target: "#projects-link",
                content: t("Navigate to all projects here."),
                placement: "bottom",
                spotlightPadding: 0,
                disableBeacon: true,
              },
            ]}
          />
        )}
      {targetsExists &&
        completedContactForm &&
        (completedPreferencesForm || skippedPreferencesForm) &&
        completedTour &&
        !completedNewFeature && (
          <NewFeatureRide
            continuous
            run={runNewFeatureLanguage}
            setRun={setRunNewFeatureLanguage}
            steps={[
              {
                title: t("New feature"),
                target: "#user-welcome",
                content: (
                  <Trans
                    i18nKey="new_feature_message"
                    components={{bold: <strong style={{color: "#242d35"}} />}}
                  />
                ),
                placement: "bottom-end",
                spotlightPadding: 0,
                disableBeacon: true,
              },
            ]}
            onClose={() => {
              setCompletedNewFeature(true)
              setRunNewFeatureLanguage(false)
            }}
          />
        )}
      <Stack
        component="section"
        direction="column"
        width="auto"
        flexGrow={1}
        gap="16px"
      >
        <Stack
          component="nav"
          direction="row"
          width="100%"
          height="58px"
          bgcolor={"theme.palette.common.white"}
          justifyContent="space-between"
          alignItems="center"
          paddingX="2rem"
          borderBottom={"solid 1px rgba(28, 28, 28, 0.1)"}
          style={{backgroundColor: theme.palette.common.white}}
        >
          <NavigationTopBar
            tabValue={currentTab}
            setTabValue={handleTabChange}
          ></NavigationTopBar>
        </Stack>
        <Stack
          component="section"
          direction="column"
          width="100%"
          paddingX="1rem"
        >
          <Stack
            id="sections-tab"
            component="section"
            direction="column"
            borderRadius="4px"
            width="100%"
            gap="1rem"
            maxHeight="calc(100vh - 168px)"
            paddingX="1rem"
            sx={{
              overflowY: "auto",
              scrollBehavior: "smooth",
              // "&::-webkit-scrollbar": {
              //   width: "4px !important",
              // },
            }}
          >
            {currentPath === "/" && (
              <>{currentTab === "Overview" ? <Overview /> : <Discover />}</>
            )}
            {currentPath.includes("/discover/") && <Outlet></Outlet>}
          </Stack>
        </Stack>
      </Stack>
      <Stack
        component="aside"
        direction={"column"}
        paddingTop={"35px"}
        width="225px"
        minWidth="225px"
        height="100%"
        borderLeft="1px solid #1c1c1c19"
        bgcolor={theme.palette.common.white}
        paddingLeft="1rem"
        paddingRight="2em"
        paddingY="1.25rem"
      >
        <AccountPanel />
        <ExpertBio />
      </Stack>
      <SupportModal
        show={showSupportModal}
        handleToggle={() => setShowSupportModal(false)}
      />
    </Stack>
  )
}
Home.displayName = "Home"

export default Home
